import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getSubscriptionPlans,
  getBillingInfo,
  updateBillingInfo as updateBillingInfoCallable,
  createCheckoutSession as createCheckoutSessionCallable,
  getBillingPortalUrl as getBillingPortalUrlCallable,
  upgradeAddOn as upgradeAddOnCallable,
  cancelAddOn as cancelAddOnCallable,
  upgradePlan as upgradePlanCallable,
  cancelPlan as cancelPlanCallable,
} from 'src/firebase';

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchSubscriptionPlans = createAsyncThunk(
  'billing/fetchPlans',
  async (companyId) => {
    const result = await getSubscriptionPlans({companyId});
    return result?.data?.plans || [];
  }
);

export const fetchBillingInfo = createAsyncThunk(
  'billing/fetchInfo',
  async (companyId) => {
    const result = await getBillingInfo({companyId});
    return result?.data;
  }
);

export const saveBillingInfo = createAsyncThunk(
  'billing/saveInfo',
  async ({companyId, invoiceDetails}) => {
    await updateBillingInfoCallable({companyId, invoiceDetails});
    return invoiceDetails;
  }
);

export const startCheckoutSession = createAsyncThunk(
  'billing/checkout',
  async ({companyId, stripePriceId, successUrl, cancelUrl}) => {
    const result = await createCheckoutSessionCallable({companyId, stripePriceId, successUrl, cancelUrl});
    return result?.data?.sessionUrl;
  }
);

export const fetchBillingPortalUrl = createAsyncThunk(
  'billing/portal',
  async ({companyId, returnUrl}) => {
    const result = await getBillingPortalUrlCallable({companyId, returnUrl});
    return result?.data?.portalUrl;
  }
);

export const upgradeAddOn = createAsyncThunk(
  'billing/upgradeAddOn',
  async ({companyId, stripePriceId}) => {
    await upgradeAddOnCallable({companyId, stripePriceId});
  }
);

export const cancelAddOn = createAsyncThunk(
  'billing/cancelAddOn',
  async ({companyId, addonType}) => {
    await cancelAddOnCallable({companyId, addonType});
  }
);

export const upgradePlan = createAsyncThunk(
  'billing/upgradePlan',
  async ({companyId, stripePriceId}) => {
    await upgradePlanCallable({companyId, stripePriceId});
  }
);

export const cancelPlan = createAsyncThunk(
  'billing/cancelPlan',
  async ({companyId}) => {
    await cancelPlanCallable({companyId});
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
  plans: [],
  billing: null,
  subscriptions: [],
  loading: false,
  error: null,
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearBillingError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchSubscriptionPlans
    builder
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.plans = action.payload;
        state.loading = false;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });

    // fetchBillingInfo
    builder
      .addCase(fetchBillingInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingInfo.fulfilled, (state, action) => {
        state.billing = action.payload?.billing || null;
        state.subscriptions = action.payload?.subscriptions || [];
        state.loading = false;
      })
      .addCase(fetchBillingInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });

    // saveBillingInfo
    builder
      .addCase(saveBillingInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveBillingInfo.fulfilled, (state, action) => {
        if (state.billing) {
          state.billing.invoiceDetails = action.payload;
        } else {
          state.billing = {invoiceDetails: action.payload, updatedAt: Date.now()};
        }
        state.loading = false;
      })
      .addCase(saveBillingInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });

    // startCheckoutSession
    builder
      .addCase(startCheckoutSession.pending, (state) => { state.loading = true; })
      .addCase(startCheckoutSession.fulfilled, (state) => { state.loading = false; })
      .addCase(startCheckoutSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });

    // fetchBillingPortalUrl
    builder
      .addCase(fetchBillingPortalUrl.pending, (state) => { state.loading = true; })
      .addCase(fetchBillingPortalUrl.fulfilled, (state) => { state.loading = false; })
      .addCase(fetchBillingPortalUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });

    // upgradeAddOn
    builder
      .addCase(upgradeAddOn.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(upgradeAddOn.fulfilled, (state) => { state.loading = false; })
      .addCase(upgradeAddOn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });

    // cancelAddOn
    builder
      .addCase(cancelAddOn.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(cancelAddOn.fulfilled, (state) => { state.loading = false; })
      .addCase(cancelAddOn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });

    // upgradePlan
    builder
      .addCase(upgradePlan.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(upgradePlan.fulfilled, (state) => { state.loading = false; })
      .addCase(upgradePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });

    // cancelPlan
    builder
      .addCase(cancelPlan.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(cancelPlan.fulfilled, (state) => { state.loading = false; })
      .addCase(cancelPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      });
  },
});

export const {clearBillingError} = billingSlice.actions;
export default billingSlice.reducer;
